import { Box, Text } from 'ink';
import React from 'react';

import { colors } from '~/styles/colors';
import { icons } from '~/styles/icons';
import { RecipesApplyResult } from '~/types/recipes-apply';

import { MetadataDisplay } from './MetadataDisplay';
import { ProcessDisplay } from './ProcessDisplay';

interface RecipesApplyResultDisplayProps {
  result: RecipesApplyResult;
  showCost?: boolean;
}

export const RecipesApplyResultDisplay: React.FC<
  RecipesApplyResultDisplayProps
> = ({ result, showCost }) => {
  const { recipe, summary, executionResults, metadata } = result;

  return (
    <ProcessDisplay title="Recipe Application Complete" status="completed">
      <Box flexDirection="column">
        <Box flexDirection="column" marginBottom={1}>
          <Text>Recipe: {recipe.getId()}</Text>
          <Text color={colors.muted}>{recipe.getSummary()}</Text>
        </Box>

        <Box flexDirection="column" marginBottom={1}>
          <Text bold>Summary:</Text>
          <Text>Total projects: {summary.totalProjects}</Text>
          <Text color={colors.success}>
            Successful: {summary.successfulProjects}
          </Text>
          {summary.failedProjects > 0 && (
            <Text color={colors.error}>Failed: {summary.failedProjects}</Text>
          )}
          {summary.skippedProjects > 0 && (
            <Text color={colors.warning}>
              Skipped: {summary.skippedProjects}
            </Text>
          )}
        </Box>

        {metadata && (
          <MetadataDisplay
            metadata={metadata}
            showCost={showCost}
            includeLabel
          />
        )}

        {executionResults.length > 0 && (
          <Box flexDirection="column" marginBottom={1}>
            <Text bold>Projects Updated:</Text>
            {executionResults.map((result, i) => (
              <Text key={i}>
                {result.success ? icons.success : icons.error}{' '}
                {result.projectPath}
                {result.error && !result.success && (
                  <Text color={colors.muted}> ({result.error})</Text>
                )}
              </Text>
            ))}
          </Box>
        )}

        {executionResults.some((result) => result.output) && (
          <Box flexDirection="column" marginBottom={1}>
            <Text bold>Recipe Results:</Text>
            {executionResults
              .filter((result) => result.output)
              .map((result, i) => (
                <Box
                  key={i}
                  flexDirection="column"
                  marginBottom={executionResults.length > 1 ? 1 : 0}
                >
                  {executionResults.length > 1 && (
                    <Text color={colors.muted}>{result.projectPath}:</Text>
                  )}
                  <Text>{result.output}</Text>
                </Box>
              ))}
          </Box>
        )}

        {recipe.getProvides().length > 0 && (
          <Box flexDirection="column" marginTop={1}>
            <Text bold>Recipe Outputs:</Text>
            {recipe.getProvides().map((key, i) => (
              <Text key={i} color={colors.muted}>
                {key}
              </Text>
            ))}
          </Box>
        )}
      </Box>
    </ProcessDisplay>
  );
};
